import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { VisibilityOutlined } from "@mui/icons-material";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";
import {
  Avatar,
  CardHeader,
  Paper,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { useTheme, alpha } from "@material-ui/core/styles";
import { getBackendUrl } from "../../config";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  ticketTime,
  lastMessagePreview,
  canOpenTicket
} from "../../pages/Moments/momentsRules";

const backendUrl = getBackendUrl();

const avatarColorFor = (id) =>
  AEROS.avatarCycle[Number(id || 0) % AEROS.avatarCycle.length];
const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
const normalizeSearch = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
    return `rgba(124,124,124,${alpha})`;
  }
  const clean = hex.replace("#", "").slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    justifyContent: "space-between",
  },
  // Grade de 4 colunas como no mockup, com as colunas alinhadas pelo topo:
  // cada card cresce com o próprio conteúdo, em vez de todos terem a mesma
  // altura fixa e sobrar espaço morto nas colunas curtas.
  container: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    alignItems: "start",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  card: {
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
    backgroundColor: theme.palette.aeros.sidebar,
    border: `1px solid ${theme.palette.aeros.border}`,
  },
  cardPending: {
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
    backgroundColor: theme.palette.aeros.sidebar,
    border: `1px solid ${withAlpha(AEROS.warning, 0.25)}`,
  },
  cardHeaderInner: {
    flex: "none",
    width: "100%",
    padding: "14px 16px",
    "& .MuiCardHeader-action": { margin: 0, alignSelf: "center" },
    borderBottom: `1px solid ${theme.palette.aeros.border}`,
    backgroundColor:
      theme.palette.type === "light"
        ? "rgba(15,20,40,0.02)"
        : "rgba(255,255,255,0.02)",
  },
  cardHeaderInnerPending: {
    flex: "none",
    width: "100%",
    padding: "14px 16px",
    "& .MuiCardHeader-action": { margin: 0, alignSelf: "center" },
    borderBottom: `1px solid ${withAlpha(AEROS.warning, 0.25)}`,
    backgroundColor: withAlpha(AEROS.warning, 0.04),
  },
  listArea: {
    display: "flex",
    flexDirection: "column",
    maxHeight: 420,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStylesSoft,
  },
  changeWarap: {
    // Largura vem da coluna da grade; largura fixa aqui quebraria o `1fr`.
    width: "100%",
    minWidth: 0,
    padding: 0,
    margin: 0,
  },
  attendantAvatar: {
    width: 38,
    height: 38,
    fontSize: 14,
    fontWeight: 700,
    color: "#07080D",
  },
  pendingAvatar: {
    width: 38,
    height: 38,
    backgroundColor: "transparent",
    border: `1px dashed ${AEROS.warning}`,
    color: AEROS.warning,
  },
  presenceDot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 9,
    height: 9,
    borderRadius: "50%",
  },
  avatarWrap: {
    position: "relative",
    display: "inline-flex",
  },
  attendantName: {
    fontSize: 13.5,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  attendantSubtitle: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  countPill: {
    marginLeft: 8,
    padding: "1px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    backgroundColor: "rgba(160,68,255,0.15)",
    color: theme.palette.type === "light" ? AEROS.stateTextOnLight.purple : "#C08BFF",
  },
  countPillPending: {
    marginLeft: 8,
    padding: "1px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    backgroundColor: withAlpha(AEROS.warning, 0.12),
    color: stateText(theme, AEROS.warning, "warning"),
  },
  connectionTag: {
    background: "rgba(255,255,255,0.06)",
    color: theme.palette.text.secondary,
    marginRight: 4,
    padding: "1px 6px",
    fontWeight: 600,
    borderRadius: 4,
    fontSize: 9.5,
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  queueTag: {
    marginRight: 4,
    padding: "1px 6px",
    fontWeight: 600,
    borderRadius: 4,
    fontSize: 9.5,
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ticketRow: {
    display: "flex",
    gap: 11,
    padding: "12px 16px",
    borderBottom: `1px solid ${
      theme.palette.type === "light"
        ? theme.palette.divider
        : "rgba(255,255,255,0.05)"
    }`,
    minWidth: 0,
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
    "&:last-child": { borderBottom: "none" },
  },
  ticketBody: { flex: 1, minWidth: 0 },
  ticketNameRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
    minWidth: 0,
  },
  ticketChips: {
    display: "flex",
    gap: 5,
    marginTop: 7,
    alignItems: "center",
    minWidth: 0,
    // Nomes reais de conexão são longos ("Atendimento TalkPulse"); sem
    // `overflow:hidden` aqui os chips empurravam o badge e o olho para fora
    // do card e criavam barra de rolagem horizontal.
    overflow: "hidden",
  },
  warningIcon: {
    fontSize: 15,
    color: stateText(theme, AEROS.warning, "warning"),
    marginLeft: 6,
  },
  ticketAvatar: {
    width: 34,
    height: 34,
    fontSize: 12.5,
    fontWeight: 700,
    color: "#07080D",
  },
  ticketName: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    // `flex:1` + `minWidth:0` no lugar de maxWidth fixo: o card muda de
    // largura conforme quantas colunas cabem, e o nome tem de acompanhar.
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lastMessage: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: 2,
  },
  lastMessageTime: {
    fontSize: 10.5,
    fontWeight: 500,
    color: theme.palette.aeros.textTertiary,
    fontVariantNumeric: "tabular-nums",
    flex: "none",
  },
  lastMessageTimeUnread: {
    color: stateText(theme, AEROS.cyan, "info"),
    fontWeight: 700,
  },
  unreadBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#07080D",
    fontWeight: 800,
    fontSize: 10.5,
    minWidth: 17,
    height: 17,
    borderRadius: 8.5,
    padding: "0 5px",
    // Empurra o badge e o olho para a direita da linha de chips.
    marginLeft: "auto",
    flex: "none",
    background: theme.palette.aerosGradient,
  },
  eyeButton: {
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 7,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    flex: "none",
    transition: "border-color 0.15s ease",
    "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
    "&:hover": {
      borderColor: AEROS.cyan,
    },
    "&:hover svg": {
      color: AEROS.cyan,
    },
  },
  eyeButtonAlone: { marginLeft: "auto" },
  eyeIcon: {
    color: theme.palette.text.secondary,
    fontSize: 15,
  },
  divider: {
    opacity: 0.05,
  },
  emptyState: {
    fontSize: 12,
    color: theme.palette.aeros.textTertiary,
    textAlign: "center",
    padding: 32,
    width: "100%",
  },
}));

const DashboardManage = ({ queueFilter, searchTerm, onStatsChange } = {}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [update, setUpdate] = useState(false);
  const [ticketNot, setTicketNot] = useState(0);
  const companyId = user.companyId;

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  // Filtro client-side por fila (Select "Filas") + busca por nome de contato
  // (campo "Buscar contato…"), ambos no header da página. Não afetam o
  // fetch/socket — só o que é exibido/contado aqui.
  const visibleTickets = useMemo(() => {
    let list = tickets;
    if (queueFilter) {
      list = list.filter((ticket) => ticket.queueId === queueFilter);
    }
    const normalizedSearch = normalizeSearch(searchTerm);
    if (normalizedSearch) {
      list = list.filter((ticket) =>
        normalizeSearch(ticket.contact?.name).includes(normalizedSearch)
      );
    }
    return list;
  }, [tickets, queueFilter, searchTerm]);

  const stats = useMemo(() => {
    const emAtendimento = visibleTickets.filter((t) => !!t.user).length;
    const aguardando = visibleTickets.filter((t) => !t.user).length;
    const naoLidas = visibleTickets.reduce(
      (sum, t) => sum + (Number(t.unreadMessages) || 0),
      0
    );
    return { emAtendimento, aguardando, naoLidas };
  }, [visibleTickets]);

  const prevStatsRef = useRef(null);
  useEffect(() => {
    if (!onStatsChange) return;
    const prev = prevStatsRef.current;
    if (
      !prev ||
      prev.emAtendimento !== stats.emAtendimento ||
      prev.aguardando !== stats.aguardando ||
      prev.naoLidas !== stats.naoLidas
    ) {
      prevStatsRef.current = stats;
      onStatsChange(stats);
    }
  }, [stats, onStatsChange]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/usersMoments");
        console.log(data);
        setTickets(data);
        setUpdate(!update);
      } catch (err) {
        if (err.response?.status !== 500) {
          toastError(err);
        } else {
          toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
        }
      }
    })();
  }, []);

  useEffect(() => {
    const companyId = user.companyId;
    console.log("socket painel");
    // const socket = socketManager.GetSocket();

    // const onConnect = () => {
    //   socket.emit("joinChatBox", `${ticketId}`);
    // }
    const onAppMessage = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        (async () => {
          try {
            const { data } = await api.get("/usersMoments");
            setTickets(data);
            setUpdate(!update);
          } catch (err) {
            if (err.response?.status !== 500) {
              toastError(err);
            } else {
              toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
            }
          }
        })();
      }
    };

    // socket.on("connect", onConnect);
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      // socket.off("connect", onConnect);
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket]);

  const renderTicketRow = (ticket) => {
    const unread = Number(ticket.unreadMessages) > 0;
    const avatarColor = avatarColorFor(ticket.contact?.id);
    const podeAbrir = canOpenTicket(ticket, user);
    const corFila = ticket.queue?.color || "#7c7c7c";
    return (
      <div className={classes.ticketRow} key={ticket.id}>
        <Avatar
          alt={ticket.contact?.name || ""}
          src={ticket.contact?.urlPicture || undefined}
          className={classes.ticketAvatar}
          style={{ backgroundColor: avatarColor }}
        >
          {initialsOf(ticket.contact?.name)}
        </Avatar>
        <div className={classes.ticketBody}>
          <div className={classes.ticketNameRow}>
            <span className={classes.ticketName} title={ticket?.contact?.name}>
              {ticket?.contact?.name}
            </span>
            <span
              className={`${classes.lastMessageTime} ${
                unread ? classes.lastMessageTimeUnread : ""
              }`}
            >
              {ticketTime(ticket.updatedAt)}
            </span>
          </div>
          <div className={classes.lastMessage}>
            {lastMessagePreview(ticket.lastMessage)}
          </div>
          <div className={classes.ticketChips}>
            <span className={classes.connectionTag}>{ticket?.whatsapp?.name}</span>
            <span
              className={classes.queueTag}
              style={{
                backgroundColor: withAlpha(corFila, 0.1),
                color: stateText(theme, corFila)
              }}
            >
              {ticket.queue?.name?.toUpperCase() ||
                i18n.t("aeros.moments.noQueue").toUpperCase()}
            </span>
            {unread && (
              <span
                className={classes.unreadBadge}
                aria-label={i18n.t("aeros.moments.unreadAria", {
                  count: ticket.unreadMessages
                })}
              >
                {ticket.unreadMessages}
              </span>
            )}
            {podeAbrir && (
              <Tooltip title={i18n.t("aeros.moments.openTicket")}>
                <span
                  className={`${classes.eyeButton} ${
                    unread ? "" : classes.eyeButtonAlone
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      history.push(`/tickets/${ticket.uuid}`);
                    }
                  }}
                >
                  <VisibilityOutlined className={classes.eyeIcon} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  };

  const Moments = useMemo(() => {
    // console.log(tickets)
    if (visibleTickets && visibleTickets.length > 0) {
      const ticketsByUser = visibleTickets.reduce((userTickets, ticket) => {
        const user = ticket.user;

        if (user) {
          const userIndex = userTickets.findIndex(
            (group) => group.user.id === user.id
          );
          if (userIndex === -1) {
            userTickets.push({
              user,
              userTickets: [ticket],
            });
          } else {
            userTickets[userIndex].userTickets.push(ticket);
          }
        }
        return userTickets;
      }, []);

      return ticketsByUser.map((group, index) => {
        const attendantColor = avatarColorFor(group.user.id);
        const isOnline = !!group.user.online;
        return (
          <div key={index} className={classes.changeWarap}>
            <div>
              <Paper elevation={0} className={classes.card}>
                <CardHeader
                  className={classes.cardHeaderInner}
                  avatar={
                    <span className={classes.avatarWrap}>
                      <Avatar
                        alt={`${group.user.name}`}
                        src={
                          group.user.profileImage
                            ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}`
                            : undefined
                        }
                        className={classes.attendantAvatar}
                        style={{ backgroundColor: attendantColor }}
                      >
                        {initialsOf(group.user.name)}
                      </Avatar>
                      <span
                        className={classes.presenceDot}
                        style={{
                          backgroundColor: isOnline
                            ? AEROS.success
                            : theme.palette.aeros.textTertiary,
                          boxShadow: `0 0 0 2px ${theme.palette.aeros.sidebar}`,
                        }}
                      />
                    </span>
                  }
                  title={
                    <span className={classes.attendantName}>
                      {group?.user?.name || `${i18n.t("momentsUser.pending")}`}
                    </span>
                  }
                  subheader={
                    <span className={classes.attendantSubtitle}>
                      {i18n.t(
                        isOnline ? "aeros.moments.online" : "aeros.moments.offline"
                      )}
                    </span>
                  }
                  action={
                    <span className={classes.countPill}>
                      {group.userTickets?.length}
                    </span>
                  }
                />
                <div className={classes.listArea}>
                  {group.userTickets.length > 0 ? (
                    group.userTickets.map((ticket) => renderTicketRow(ticket))
                  ) : (
                    <span className={classes.emptyState}>
                      {i18n.t("aeros.moments.empty")}
                    </span>
                  )}
                </div>
              </Paper>
            </div>
          </div>
        );
      });
    } else {
      return null;
    }
  }, [update, visibleTickets, classes]);

  const MomentsPending = useMemo(() => {
    if (visibleTickets && visibleTickets.length > 0) {
      const pendingTickets = visibleTickets.filter((ticket) => !ticket.user);

      return (
        <div className={classes.changeWarap}>
          <div>
            <Paper elevation={0} className={classes.cardPending}>
              <CardHeader
                className={classes.cardHeaderInnerPending}
                avatar={
                  <Avatar className={classes.pendingAvatar}>
                    <HourglassEmptyOutlinedIcon fontSize="small" />
                  </Avatar>
                }
                title={
                  <span className={classes.attendantName}>
                    {i18n.t("aeros.moments.waiting")}
                    <ReportProblemOutlinedIcon className={classes.warningIcon} />
                  </span>
                }
                subheader={
                  <span className={classes.attendantSubtitle}>
                    {i18n.t("aeros.moments.waitingSubtitle")}
                  </span>
                }
                action={
                  <span className={classes.countPillPending}>
                    {pendingTickets?.length}
                  </span>
                }
              />
              <div className={classes.listArea}>
                {pendingTickets.length > 0 ? (
                  pendingTickets.map((ticket) => renderTicketRow(ticket))
                ) : (
                  <span className={classes.emptyState}>
                    {i18n.t("aeros.moments.empty")}
                  </span>
                )}
              </div>
            </Paper>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }, [update, visibleTickets, classes]);

  if (!tickets || tickets.length === 0) {
    return (
      <span className={classes.emptyState}>
        Sem atendimentos no momento
      </span>
    );
  }

  if (visibleTickets.length === 0) {
    return (
      <span className={classes.emptyState}>
        {i18n.t("aeros.moments.empty")}
        {queueFilter || normalizeSearch(searchTerm)
          ? ` ${i18n.t("aeros.moments.emptyFilter")}`
          : ""}
      </span>
    );
  }

  return (
    <Fragment>
      <div className={classes.container}>
        {Moments}
        {MomentsPending}
      </div>
    </Fragment>
  );
};

export default DashboardManage;
